const fmtEur = (n: number): string =>
  new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR' }).format(n || 0);

interface EindeWerkenPromptProps {
  /** How many offertelijnen this vordering closes out (all of the open ones). */
  lineCount: number;
  /** The euro this vordering bills — the picker's own preview total. */
  total: number;
  /** The fase that would be marked «einde werken» (the latest today/past fase of
   *  the selection). Null when the selection has none — then the marker has to be
   *  set by hand in the planning, and the prompt says so instead of implying a flip. */
  eindeFaseName: string | null;
  onEindeWerken: () => void;
  onGewoon: () => void;
  onCancel: () => void;
}

/**
 * "Alles gevorderd — is dit einde werken?"
 *
 * Fires on submit when the vordering leaves no headroom on any offertelijn while
 * the type toggle still says «Gevorderd». Billing the last euro without the
 * einde-werken flag is almost always the forgotten toggle: the vordering is named
 * «Vordering N» instead of «Einde werken» and the closing fase never gets its
 * «einde werken» marker on the planning board. The PL answers the question once;
 * both answers create the vordering, so this is a fork, not a blocker.
 */
export function EindeWerkenPrompt({
  lineCount, total, eindeFaseName, onEindeWerken, onGewoon, onCancel,
}: EindeWerkenPromptProps) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="einde-werken-prompt-title"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-gray-100 px-5 py-4">
          <h2 id="einde-werken-prompt-title" className="text-base font-bold text-td-navy">
            Alles gevorderd — is dit einde werken?
          </h2>
          <p className="mt-1 text-xs text-td-muted">
            Met deze vordering {lineCount === 1 ? 'staat de offertelijn' : `staan alle ${lineCount} offertelijnen`}
            {' '}volledig gevorderd ({fmtEur(total)}), maar het type staat op «Gevorderd».
          </p>
        </div>

        <div className="space-y-2 px-5 py-4 text-xs text-td-muted">
          <p>
            <span className="font-semibold text-td-navy">Einde werken</span> noemt de vordering
            «Einde werken»{eindeFaseName
              ? <> en zet <span className="font-semibold text-td-navy">{eindeFaseName}</span> op «einde werken» in de planning.</>
              : <>. Er is geen fase van vandaag of het verleden geselecteerd, dus de eindfase moet je zelf markeren in de planning.</>}
          </p>
          <p>
            <span className="font-semibold text-td-navy">Gewone vordering</span> laat alles zoals het
            nu staat — het project blijft zonder einde-werken markering.
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-100 bg-gray-50 px-5 py-3">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg px-3 py-2 text-sm font-semibold text-td-muted hover:bg-gray-100"
          >
            Terug
          </button>
          <button
            type="button"
            onClick={onGewoon}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-td-navy hover:bg-gray-50"
          >
            Nee, gewone vordering
          </button>
          <button
            type="button"
            onClick={onEindeWerken}
            className="rounded-lg bg-td-blue px-4 py-2 text-sm font-semibold text-white transition hover:bg-td-blue/90"
          >
            Ja, einde werken
          </button>
        </div>
      </div>
    </div>
  );
}
